
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CloudUpload,
  Image as ImageIcon,
  X,
  ShoppingCart,
  CheckCircle2,
} from "lucide-react";

import { usePrescriptionCart } from "../../context/pharmacy/PrescriptionCartContext";
import { fileToBase64 } from "../../utils/fileUtils";
import "./ImagePrescriptionForm.css";

export default function ImagePrescriptionForm() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const { addItem } = usePrescriptionCart();

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!image) {
      setPreview("");
      return;
    }

    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setError("");

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image.");
      event.target.value = "";
      return;
    }

    // Maximum image size: 5 MB
    if (file.size > 5 * 1024 * 1024) {
      setError("The image must be smaller than 5 MB.");
      event.target.value = "";
      return;
    }

    setImage(file);
  };

  const handleRemoveImage = () => {
    setImage(null);
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAdd = async () => {
    if (!image) {
      setError("Please select your prescription image first.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const imageBase64 = await fileToBase64(image);

      addItem({
        type: "imagePrescription",
        image: imageBase64,
        fileName: image.name,
        mimeType: image.type,
      });

      navigate("/dashboard/pharmacy/prescription-cart");
    } catch (err) {
      console.error("Failed to add prescription:", err);
      setError("Could not add the prescription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="image-prescription">
      <div className="image-prescription-heading">
        <h2>بارگذاری نسخه</h2>
        <p>
         تصویری واضح از نسخه خود بارگذاری کنید تا به سبد خریدتان اضافه شود.
        </p>
      </div>

      <div className="prescription-upload-box">
        {!image ? (
          <>
            <div className="prescription-upload-icon">
              <CloudUpload size={76} strokeWidth={1.8} />
            </div>

            <h3>تصویر نسخه</h3>

            <p className="prescription-upload-description">
             لطفاً اطمینان حاصل کنید که تمامِ نسخه قابل مشاهده و واضح باشد، و عکس آن دقیقاً از بالا گرفته شده باشد.
            </p>

            <p className="prescription-upload-hint">
              JPG, PNG or WEBP · حداکثر حجم 5 MB
            </p>

            <input
              ref={fileInputRef}
              id="prescription-image-input"
              className="prescription-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
            />

            <button
              type="button"
              className="prescription-choose-button"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImageIcon size={19} />
             انتخاب تصویر نسخه
            </button>
          </>
        ) : (
          <div className="prescription-preview-container">
            <div className="prescription-preview-success">
              <CheckCircle2 size={20} />
             تصویر با موفقیت انتخاب شد
            </div>

            <div className="prescription-preview-image-wrapper">
              <img
                src={preview}
                alt="Prescription preview"
                className="prescription-preview-image"
              />

              <button
                type="button"
                className="prescription-remove-image"
                onClick={handleRemoveImage}
                aria-label="Remove selected image"
                title="Remove image"
              >
                <X size={19} />
              </button>
            </div>

            <p className="prescription-file-name" title={image.name}>
              {image.name}
            </p>

            <p className="prescription-file-size">
              {(image.size / 1024).toFixed(0)} KB
            </p>

            <button
              type="button"
              className="prescription-choose-button prescription-change-button"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImageIcon size={18} />
             تغییر تصویر
            </button>

            <input
              ref={fileInputRef}
              id="prescription-image-input"
              className="prescription-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
            />
          </div>
        )}
      </div>

      {error && (
        <p className="prescription-upload-error" role="alert">
          {error}
        </p>
      )}

      <div className="prescription-upload-actions">
        <button
          type="button"
          className="prescription-add-cart-button"
          onClick={handleAdd}
          disabled={!image || loading}
        >
          <ShoppingCart size={20} />
          {loading ? "Adding to Cart..." : "افزودن به سبد خرید"}
        </button>
      </div>

      <div className="prescription-upload-notice">
        <div className="prescription-notice-heading">
          <span className="prescription-notice-info">i</span>
          <strong>دستورالعمل‌های بارگذاری تصویر</strong>
        </div>

        <ul>
          <li>برای سفارش داروهای دارای نسخه حتما تصویر اصل نسخه دارای مهر به صورت واضح بارگزاری شود
</li>
          <li>کلیه مراحل تأیید نسخه و قیمت گذاری داروها طبق قوانین و ضوابط فنی داروخانه ها در سراسر کشور انجام می پذیرد
</li>
        
        </ul>
      </div>
    </section>
  );
}
